// Genesis Utilities
import ParameterValidator from '@mvps-genesis/parameter-validator';
import uid from '@mvps-genesis/uid';

// Class dependencies
import InputPasswordToggle from './InputPasswordToggle';
import InputPasswordRequirement from './InputPasswordRequirement';

/*
 *--------------------------------------------------------------------------
 * Input Password
 *--------------------------------------------------------------------------
 *
 * Class to add visual password requirement component
 *
 */

export default class InputPassword {

	/**
	 * The form element being validated.
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 *
	 * @var	String Indicates the input & surrounding elements are in password mode (i.e. input[type="password"])
	 */
	MODE_PASSWORD;

	/**
	 *
	 * @var	String Indicates the input & surrounding elements are in text mode (i.e. input[type="text"])
	 */
	MODE_TEXT;

	/**
	 *
	 * @var	String Set to either `this.MODE_PASSWORD` or `this.MODE_TEXT`
	 */
	mode;

	/**
	 *
	 * @var	HTMLInputElement Reference to the input password DOM element
	 */
	element;

	/**
	 *
	 * @var	HTMLDivElement Reference to the parent DOM element of the input password DOM element
	 */
	container;

	/**
	 *
	 * @var	HTMLDivElement Reference to the ARIA region DOM element. Used to announce what type of mode the input password is in
	 */
	region;

	/**
	 *
	 * @var	Object Reference to the `InputPasswordToggle` class
	 */
	InputPasswordToggle;

	/**
	 *
	 * @var	Object Reference to the `InputPasswordRequirement` class
	 */
	InputPasswordRequirement;

	/**
	 * Constructor of `InputPassword`
	 * @param  {HTMLInputElement} element
	 */
	constructor (element) {
		this.#validator = new ParameterValidator(this);

		// Constants
		this.MODE_PASSWORD = 'password';
		this.MODE_TEXT = 'text';
		this.mode = this.MODE_PASSWORD;

		// DOM Elements
		this.element = this.#validator.isElement(`element`, element);
		this.container = this.createContainerElement();
		this.region = this.createAriaLiveRegionElement();

		// Instances

		// Exclude the toggle for IE and Edge
		this.InputPasswordToggle = new InputPasswordToggle();
		this.InputPasswordRequirement = new InputPasswordRequirement();

		// Init
		this.init();
	}

	/**
	 * Entry point function
	 */
	init () {
		this.bindEvents();
		this.setAttributeID();
		this.setAttributeAriaControl();
		this.buildDOM();
	}

	/**
	 * Binds all events to DOM elements
	 */
	bindEvents () {
		// Set as a referenced to be removed
		this.onFocus = this.onFocus.bind(this);

		this.element.form.addEventListener('submit', this.onSubmit.bind(this));
		this.container.addEventListener('click', this.onClick.bind(this));

		// Only bind if we need to show the password requirement element
		if (this.hasPasswordRequirement()) {
			this.element.addEventListener('input', this.onInput.bind(this));
			this.element.addEventListener('focus', this.onFocus);
		}
	}

	/**
	 * Builds the DOM for the input password toggle functionality
	 */
	buildDOM () {
		this.element.parentElement.replaceChild(this.container, this.element);

		this.container.appendChild(this.element);

		if (this.InputPasswordToggle.isAllowed()) {
			this.container.appendChild(this.InputPasswordToggle.element);
		}

		this.container.appendChild(this.region);

		if (this.hasPasswordRequirement()) {
			this.InputPasswordRequirement.settings.forEach(setting => {
				this.InputPasswordRequirement.element.appendChild(setting.element);
			});

			this.container.insertAdjacentElement('afterend', this.InputPasswordRequirement.element);
		}
	}

	/**
	 * Determine if the instance `InputPasswordRequirement` was initiated. This instance will only be available for password inputs that require the user to create an account.
	 * @return {Boolean}
	 */
	hasPasswordRequirement () {
		return typeof this.InputPasswordRequirement !== 'undefined' && this.element.classList.contains('js-input-password-requirement');
	}

	/**
	 * Sets the `this.mode` to the value of the `this.element`'s `input[type]`
	 */
	setModeFromInputType () {
		if (this.element.getAttribute('type').toLowerCase() === 'password') {
			this.mode = this.MODE_PASSWORD;
		} else {
			this.mode = this.MODE_TEXT;
		}
	}

	/**
	 * Sets a unique identifier. Creates a unique reference for †he ARIA attributes
	 */
	setAttributeID () {
		if (this.element.getAttribute('id') === null) {
			this.element.setAttribute('id', uid());
		}
	}

	/**
	 * Sets attribute `aria-controls` on `InputPasswordToggle` by using the ID
	 */
	setAttributeAriaControl () {
		if (!this.InputPasswordToggle.isAllowed()) {
			return;
		}

		this.InputPasswordToggle.element.setAttribute('aria-controls', this.element.getAttribute('id'));
	}

	/**
	 * Returns the mode of the input and surrounding elements
	 * @return {String}
	 */
	getMode () {
		return this.mode;
	}

	/**
	 * Creates the parent/container element that will wrap the password input, toggle button, and ARIA region element
	 * @return {HTMLDivElement}
	 */
	createContainerElement () {
		const containerElement = document.createElement('div');
		containerElement.classList.add('g-form-input-password');

		return containerElement;
	}

	/**
	 * Creates the ARIA region element. This element is used to announce the status of the password input
	 * @return {HTMLDivElement}
	 */
	createAriaLiveRegionElement () {
		const regionElement = document.createElement('div');
		regionElement.setAttribute('aria-live', 'polite');
		regionElement.setAttribute('role', 'alert');

		regionElement.classList.add('u-hide-visually');
		regionElement.textContent = 'Your password is hidden';

		return regionElement;
	}

	/**
	 * Bound to `this.element.form` submission callback handler. Change the type back to password to avoid the browser from remembering the password
	 */
	onSubmit () {
		this.element.setAttribute('type', 'password');
	}

	/**
	 * Bound to `this.container` click callback handler. When the `InputPasswordToggle.element` is clicked adjust the input's type, update the toggle element state, and update the ARIA region text
	 * @param  {Event<Object>} event
	 */
	onClick (event) {
		const {target} = event;

		event.stopPropagation();

		if (target !== this.InputPasswordToggle.element) {
			return;
		}

		this.toggleViewOfMode();
	}

	/**
	 * Toggles the input and surrounding elements operating as a text or password input
	 */
	toggleViewOfMode () {
		if (this.getMode() === this.MODE_PASSWORD) {
			this.setViewIntoTextMode();
		} else {
			this.setViewIntoPasswordMode();
		}

		this.setModeFromInputType();
	}

	/**
	 * Sets the DOM of the input and surrounding elements to operate as a text input
	 */
	setViewIntoTextMode () {
		this.element.setAttribute('type', 'text');
		this.element.setAttribute('autocomplete', 'off');

		this.region.textContent = 'Your password is shown';

		if (this.InputPasswordToggle.isAllowed()) {
			this.InputPasswordToggle.setContextTypeText();
		}
	}

	/**
	 * Sets the DOM of the input and surrounding elements to operate as a password input
	 */
	setViewIntoPasswordMode () {
		this.element.setAttribute('type', 'password');
		this.element.removeAttribute('autocomplete');

		this.region.textContent = 'Your password is hidden';

		if (this.InputPasswordToggle.isAllowed()) {
			this.InputPasswordToggle.setContextTypePassword();
		}
	}

	/**
	 * Bound to `this.element` on input. Will update the requirement DOM element state.
	 * @param  {Event<Object>} event
	 */
	onInput (event) {
		event.stopPropagation();

		this.InputPasswordRequirement.settings.forEach(setting => {
			const pattern = new RegExp(setting.requirement.pattern);

			if (pattern.test(this.element.value)) {
				setting.valid();
			} else {
				setting.invalid();
			}
		});
	}

	/**
	 * Bound to `this.element` on focus. Will show the requirement element if the password input
	 * is a child of a form that is being used to create an account
	 */
	onFocus () {
		this.InputPasswordRequirement.showElement();
		this.element.removeEventListener('focus', this.onFocus);
	}
}
